import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Input, Loading, NumberInput, Segmented, Select, Textarea } from '../components/ui.jsx';
import { EntityPicker } from '../components/EntityPicker.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { liters, money, num, today } from '../lib/format.js';

const blank = {
  batch_no: '',
  purchased_on: today(),
  supplier_id: null,
  supplier_name: '',
  tons: '',
  liters: '',
  tank_id: '',
  currency: 'IQD',
  price_per_ton: '',
  payment_type: 'debt',
  notes: '',
};

export default function PurchaseForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const suppliers = useApi('/api/suppliers');
  const tanks = useApi('/api/tanks');
  const existing = useApi(isEdit ? `/api/purchases/${id}` : null, null, { enabled: isEdit });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // A new purchase gets the next batch number filled in, but it stays editable
  // for facilities that number their own paperwork.
  useEffect(() => {
    if (isEdit) return;
    api
      .get('/api/purchases/next-batch-no', { date: form.purchased_on })
      .then(({ batch_no }) => setForm((f) => (f.batch_no ? f : { ...f, batch_no })))
      .catch(() => {});
  }, [isEdit, form.purchased_on]);

  useEffect(() => {
    if (!existing.data) return;
    const p = existing.data;
    setForm({
      batch_no: p.batch_no,
      purchased_on: p.purchased_on,
      supplier_id: p.supplier_id,
      supplier_name: p.supplier_name,
      tons: p.tons,
      liters: p.liters,
      tank_id: String(p.tank_id),
      currency: p.currency,
      price_per_ton: Number(p.price_per_ton) ? p.price_per_ton : '',
      payment_type: p.payment_type,
      notes: p.notes || '',
    });
  }, [existing.data]);

  const selectedTank = useMemo(
    () => (tanks.data || []).find((tank) => String(tank.id) === String(form.tank_id)),
    [tanks.data, form.tank_id],
  );

  // Litres per tonne is the quickest way to catch a slipped decimal point.
  const density = useMemo(() => {
    const tonnes = Number(form.tons);
    const litres = Number(form.liters);
    return tonnes > 0 && litres > 0 ? Math.round(litres / tonnes) : null;
  }, [form.tons, form.liters]);

  const room = selectedTank
    ? Number(selectedTank.capacity_liters) -
      Number(selectedTank.current_liters) +
      (isEdit && existing.data && existing.data.tank_id === selectedTank.id ? Number(existing.data.liters) : 0)
    : null;
  const overflows = room !== null && Number(form.liters) > room;

  // The amount charged to the supplier is always tons * price_per_ton - shown
  // here for confirmation before saving, computed exactly by the server.
  const computedAmount = useMemo(() => {
    const tons = Number(form.tons);
    const price = Number(form.price_per_ton);
    return tons > 0 && price > 0 ? tons * price : null;
  }, [form.tons, form.price_per_ton]);

  const canSave =
    form.purchased_on &&
    (form.supplier_id || form.supplier_name) &&
    Number(form.tons) > 0 &&
    Number(form.liters) > 0 &&
    form.tank_id &&
    Number(form.price_per_ton) > 0 &&
    !overflows;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      ...form,
      tank_id: Number(form.tank_id),
    };
    try {
      if (isEdit) {
        await api.patch(`/api/purchases/${id}`, payload);
        toast.success(t.purchase.updated);
        navigate(`/buy/${id}/receipt`, { replace: true });
      } else {
        const created = await api.post('/api/purchases', payload, {
          queueOffline: true,
          label: `${t.dashboard.purchase} ${form.batch_no || ''}`.trim(),
        });
        if (created.queued) {
          toast.warn(t.purchase.queued);
          navigate('/buy', { replace: true });
        } else {
          toast.success(t.purchase.saved(created.batch_no));
          navigate(`/buy/${created.id}/receipt`, { replace: true });
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (isEdit && existing.loading && !existing.data) return <Loading />;

  return (
    <>
      <PageHeader
        title={isEdit ? t.purchase.editTitle : t.purchase.newTitle}
        subtitle={t.purchase.formSubtitle}
        back="/buy"
      />

      <form className="page" onSubmit={submit}>
        {error && <Alert tone="error">{error}</Alert>}

        <Card title={t.purchase.load}>
          <div className="form-grid cols-2">
            <Input
              label={t.purchase.batchNo}
              required
              dir="ltr"
              value={form.batch_no}
              onChange={(e) => set({ batch_no: e.target.value })}
            />
            <Input
              label={t.common.date}
              required
              type="date"
              value={form.purchased_on}
              onChange={(e) => set({ purchased_on: e.target.value })}
            />
          </div>

          <EntityPicker
            label={t.purchase.supplier}
            required
            items={suppliers.data || []}
            value={form.supplier_id}
            name={form.supplier_name}
            onChange={({ id: supplierId, name }) => set({ supplier_id: supplierId, supplier_name: name })}
            createLabel={t.purchase.addSupplier}
            hint={t.purchase.supplierHint}
          />

          <div className="form-grid cols-2">
            <NumberInput
              label={t.purchase.tons}
              required
              value={form.tons}
              onChange={(tonnes) => set({ tons: tonnes })}
              placeholder="0.000"
            />
            <NumberInput
              label={t.purchase.liters}
              required
              value={form.liters}
              onChange={(litres) => set({ liters: litres })}
              placeholder="0.000"
              hint={density ? t.purchase.densityHint(num(density)) : t.purchase.litersHint}
            />
          </div>

          <Select
            label={t.purchase.destinationTank}
            required
            placeholder={t.purchase.chooseTank}
            value={form.tank_id}
            onChange={(e) => set({ tank_id: e.target.value })}
            options={(tanks.data || []).map((tank) => ({
              value: tank.id,
              label: `${tank.name} — ${liters(tank.current_liters)} / ${liters(tank.capacity_liters)}`,
            }))}
            hint={room !== null ? t.purchase.roomFor(liters(room)) : undefined}
          />
          {overflows && <Alert tone="error">{t.purchase.overflow(selectedTank.name)}</Alert>}
        </Card>

        <Card title={t.purchase.price}>
          <Segmented
            label={t.shared.pricedIn}
            value={form.currency}
            onChange={(currency) => set({ currency })}
            options={[
              { value: 'IQD', label: t.report.iqd },
              { value: 'USD', label: t.report.usd },
            ]}
            hint={t.shared.currencyHint}
          />
          <NumberInput
            label={t.purchase.pricePerTon}
            required
            value={form.price_per_ton}
            onChange={(price_per_ton) => set({ price_per_ton })}
            placeholder="0"
            hint={t.purchase.pricePerTonHint}
          />
          {computedAmount !== null && (
            <Alert tone="info">{t.purchase.computedAmount(money(computedAmount, form.currency))}</Alert>
          )}
          <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
        </Card>

        <div className="btn-row end no-print">
          <button type="button" className="btn" onClick={() => navigate('/buy')} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={!canSave || busy}>
            {busy ? <span className="spinner" /> : isEdit ? t.common.saveChanges : t.purchase.save}
          </button>
        </div>
      </form>
    </>
  );
}
